import { timeZoneDateYmd, zonedMidnightToUtcIso, zonedEndOfDayToUtcIso } from "./date-utils";

export type EventTiming = {
  start_time?: string | null;
  end_time?: string | null;
  all_day?: boolean | null;
  all_day_date?: string | null;
  all_day_end_date?: string | null;
};

/** Compare the date shown to members, using current organization midnight. */
export function eventStartMillis(event: EventTiming, timezone: string): number {
  if (event.all_day && event.all_day_date) {
    return new Date(zonedMidnightToUtcIso(event.all_day_date, timezone)).getTime();
  }
  return event.start_time ? new Date(event.start_time).getTime() : 0;
}

export function eventStatusAt(event: EventTiming, timezone: string, now: Date): "scheduled" | "in_progress" | "completed" {
  if (event.all_day) {
    // new Date(null) is the 1970 epoch, which would read an all-day item with no date at
    // all as long completed. No data path produces one today; if one appears, say so (#6392).
    if (!event.all_day_date && !event.start_time) {
      throw new Error("eventStatusAt: an all-day event needs all_day_date or start_time");
    }
    const start = event.all_day_date ?? timeZoneDateYmd(timezone, new Date(event.start_time as string));
    const end = event.all_day_end_date ?? (event.end_time ? timeZoneDateYmd(timezone, new Date(event.end_time)) : start);
    const today = timeZoneDateYmd(timezone, now);
    return today < start ? "scheduled" : today <= end ? "in_progress" : "completed";
  }
  const start = new Date(event.start_time!).getTime();
  const end = new Date(event.end_time!).getTime();
  return now.getTime() < start ? "scheduled" : now.getTime() <= end ? "in_progress" : "completed";
}

/** Canonical end dates are inclusive through the organization's entire day. */
export function eventEndMillis(event: EventTiming, timezone: string): number {
  if (event.all_day) {
    // Always the end of an organization day, never the raw end_time instant: an all-day item
    // missing its dates used to fall through to the timed branch and end hours or a day off
    // (#6392). The canonical dates come first, as before. The instants are only a fallback,
    // read in the organization's timezone as eventStatusAt reads them. NaN when nothing is usable.
    const endDate =
      event.all_day_end_date ??
      event.all_day_date ??
      (event.end_time ? timeZoneDateYmd(timezone, new Date(event.end_time)) : null) ??
      (event.start_time ? timeZoneDateYmd(timezone, new Date(event.start_time)) : null);
    return endDate ? new Date(zonedEndOfDayToUtcIso(endDate, timezone)).getTime() : NaN;
  }
  return event.end_time ? new Date(event.end_time).getTime() : NaN;
}
